import type { EmployeesRequestBody } from "@/features/employees/types/employees.types";
import type { JobResult } from "@/features/jobs/types/jobs.types";

type FormatCommentType = (str?: string | null) => JobResult[];

export const formatCommentsToJobResults: FormatCommentType = (str) => {
  if (!str) return [];
  return str
    .replace(/:/gi, ":,")
    .split(",")
    .map((subsStr, index) => ({
      id: index.toString(),
      subject: "",
      message: subsStr,
    }));
};

export const formatPlural = (count: number, word: string) => {
  const isSingular: boolean = new Intl.PluralRules("fr-FR").select(count) === "one";
  return isSingular ? word : `${word}s`;
};

export const formatEmployeesRequestBody = (url: string, sortParams?: string[]): EmployeesRequestBody => {
  const queryString = url.includes("?") ? url.split("?")[1] : url;
  const params = new URLSearchParams(queryString);

  const result: EmployeesRequestBody = {
    page: parseInt(params.get("page") ?? "1"),
    fields: params.get("fields") ?? undefined,
    exclude_fields: "avatar",
    q: {},
  };

  const groupingRegex = /^q\[g\]\[(\d+)\]\[([^\]]+)\]$/;

  params.forEach((value, key) => {
    const groupingMatch = key.match(groupingRegex);
    if (groupingMatch) {
      const index = parseInt(groupingMatch[1]);
      const predicate = groupingMatch[2];
      if (!result.q.g) result.q.g = [];
      if (!result.q.g[index]) result.q.g[index] = {};
      result.q.g[index][predicate] = value;
      return;
    }
    // Handle array parameters like q[organization_site_id_in][]
    if (key.endsWith("[]")) {
      const cleanKey = key.replace(/^q\[|\]\[\]$/g, "");
      if (!result.q[cleanKey]) {
        result.q[cleanKey] = [];
      }
      if (Array.isArray(result.q[cleanKey])) {
        result.q[cleanKey].push(value);
      }
    }
    else {
      const cleanKey = key.replace(/^q\[|\]$/g, "");
      result.q[cleanKey] = value;
    }
  });

  if (sortParams && sortParams.length > 0) {
    result.q.s = sortParams;
  }

  return result;
};

export const formatCurrency = (value: number, fallback = ""): string => {
  if (value === null || value === undefined) return fallback;
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " \u20AC";
};

export const formatDateLocale = (dateStr: string | null, fallback = "\u2014"): string => {
  if (!dateStr) return fallback;
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

export const formatStringToCurrency = (numStr?: string): string => {
  const num = Number(numStr);
  if (!numStr || isNaN(num) || num === 0) return "";
  return num.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " €";
};

export const updateSet = <T>(previousSet: Set<T>, values: T[]) => {
  const newSet = new Set(previousSet);
  values.forEach((value) => {
    const isAlreadySelected = previousSet.has(value);
    if (isAlreadySelected) {
      newSet.delete(value);
    }
    else {
      newSet.add(value);
    }
  });
  return newSet;
};

/**
 * Build a one-line postal address for display in a table cell.
 *
 * Shared by the employee home address and the card reissuance delivery address:
 * the separator and blank-skipping rules are one display decision, not two.
 *
 * @example
 * formatPostalAddressLine({ lines: ["12 BIS RUE DES CLERCS"], zipCode: "38000", city: "GRENOBLE" })
 * // "12 BIS RUE DES CLERCS, 38000 GRENOBLE"
 */
export const formatPostalAddressLine = ({
  lines,
  zipCode,
  city,
}: {
  lines: (string | null | undefined)[]
  zipCode?: string | null
  city?: string | null
}): string => {
  const streetLines = lines.map((line) => line?.trim() ?? "").filter(Boolean);

  const cityLine = [zipCode, city]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" ");

  return [...streetLines, cityLine].filter(Boolean).join(", ");
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
